import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, ChevronRight, ChevronDown, Check, Loader2 } from 'lucide-react';
import { OpenOpportunitySummary } from '../../lib/crm/opportunityLinking';
import OpportunityNextFollowUpHeader from '../crm/OpportunityNextFollowUpHeader';

interface ConversationLeadBannerProps {
  opportunities: OpenOpportunitySummary[];
  onOpenOpportunity?: (opportunityId: number) => void;
  /** Schedule (or clear, when null) the next follow-up for an opportunity. */
  onSaveFollowUp?: (opportunityId: number, followUpAt: string | null) => Promise<void>;
  /** Mark the currently-scheduled follow-up complete for an opportunity. */
  onCompleteFollowUp?: (opportunityId: number) => Promise<void>;
  /** Move an opportunity to a different pipeline stage directly from the banner. */
  onChangeStage?: (opportunityId: number, newStageId: number) => Promise<void>;
}

function formatOpportunityValue(value: number): string | null {
  if (!value) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

interface StagePillProps {
  stages: Array<{ id: number; name: string }>;
  currentStageId: number;
  currentStageName: string;
  onChange: (newStageId: number) => Promise<void>;
}

type MenuCoords = {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
};

function StagePill({ stages, currentStageId, currentStageName, onChange }: StagePillProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null);

  const updateMenuPosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const gap = 6;
    const width = Math.min(window.innerWidth * 0.72, 240);
    const pad = 8;
    const spaceBelow = window.innerHeight - rect.bottom - pad;
    const spaceAbove = rect.top - pad;
    const openBelow = spaceBelow >= 160 || spaceBelow >= spaceAbove;
    const maxHeight = Math.max(
      120,
      Math.min(openBelow ? spaceBelow - gap : spaceAbove - gap, window.innerHeight * 0.5),
    );

    let left = rect.right - width;
    left = Math.max(pad, Math.min(left, window.innerWidth - width - pad));

    if (openBelow) {
      setMenuCoords({ top: rect.bottom + gap, left, width, maxHeight });
    } else {
      setMenuCoords({
        bottom: window.innerHeight - rect.top + gap,
        left,
        width,
        maxHeight,
      });
    }
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuCoords(null);
      return;
    }
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const handleSelect = async (stageId: number) => {
    if (stageId === currentStageId) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await onChange(stageId);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const label = currentStageName || 'Stage';
  const interactive = stages.length > 0;

  return (
    <div ref={rootRef} className="relative flex-shrink-0">
      {/* iOS tinted capsule: clearly a control (chevron always visible) */}
      <button
        ref={buttonRef}
        type="button"
        disabled={!interactive || saving}
        onClick={(e) => {
          e.stopPropagation();
          if (interactive && !saving) setOpen((v) => !v);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`inline-flex min-h-[28px] items-center gap-1 max-w-[42vw] sm:max-w-[12rem] rounded-full bg-blue-500/10 pl-3 pr-2 py-1 text-[12px] font-semibold text-blue-600 leading-none transition-all duration-150 active:scale-[0.96] active:bg-blue-500/20 disabled:opacity-60 ${
          interactive ? 'hover:bg-blue-500/15' : ''
        }`}
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <span className="truncate">{label}</span>
        {saving ? (
          <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin" />
        ) : interactive ? (
          <ChevronDown className="h-3 w-3 flex-shrink-0 -mr-0.5 opacity-70" strokeWidth={2.5} />
        ) : null}
      </button>

      {open && menuCoords
        ? createPortal(
            /* Portal + fixed coords — conversation overflow:hidden was clipping absolute menus off-screen */
            <div
              ref={menuRef}
              role="listbox"
              className="fixed z-[100080] overflow-y-auto rounded-2xl border border-black/[0.08] bg-white/95 shadow-[0_10px_40px_rgba(0,0,0,0.14)] backdrop-blur-xl divide-y divide-black/[0.05]"
              style={{
                top: menuCoords.top,
                bottom: menuCoords.bottom,
                left: menuCoords.left,
                width: menuCoords.width,
                maxHeight: menuCoords.maxHeight,
                WebkitBackdropFilter: 'blur(20px)',
                WebkitOverflowScrolling: 'touch',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {stages.map((stage) => {
                const isCurrent = stage.id === currentStageId;
                return (
                  <button
                    key={stage.id}
                    type="button"
                    role="option"
                    aria-selected={isCurrent}
                    onClick={() => void handleSelect(stage.id)}
                    className={`flex w-full min-h-[42px] items-center gap-2 px-3.5 py-2 text-left text-[13px] transition-colors touch-manipulation ${
                      isCurrent ? 'font-semibold text-blue-600' : 'text-gray-800 active:bg-black/[0.04]'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <span className="flex-1 min-w-0 truncate">{stage.name}</span>
                    {isCurrent ? <Check className="h-4 w-4 flex-shrink-0 text-blue-600" strokeWidth={2.5} /> : null}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export default function ConversationLeadBanner({
  opportunities,
  onOpenOpportunity,
  onSaveFollowUp,
  onCompleteFollowUp,
  onChangeStage,
}: ConversationLeadBannerProps) {
  if (!opportunities.length) return null;

  const showFollowUp = Boolean(onSaveFollowUp);

  return (
    <div
      className="relative z-40 flex-shrink-0 border-b border-black/[0.06] bg-[#f2f2f7]/95 backdrop-blur-xl"
      style={{ WebkitBackdropFilter: 'blur(20px)' }}
    >
      <div className="px-2.5 sm:px-3 py-1.5 space-y-1">
        {opportunities.map((opportunity) => {
          const valueLabel = formatOpportunityValue(opportunity.value);
          const canOpen = Boolean(onOpenOpportunity);
          const canChangeStage = Boolean(onChangeStage) && opportunity.stages.length > 0;

          return (
            /* One cohesive iOS card per lead. The ENTIRE card is the tap
               target that opens the opportunity details; the two controls on
               the bottom row stop propagation so they work in place. */
            <div
              key={opportunity.id}
              role={canOpen ? 'button' : undefined}
              tabIndex={canOpen ? 0 : undefined}
              onClick={canOpen ? () => onOpenOpportunity!(opportunity.id) : undefined}
              onKeyDown={
                canOpen
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpenOpportunity!(opportunity.id);
                      }
                    }
                  : undefined
              }
              className={`rounded-2xl bg-white border border-black/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.05)] px-3 py-1.5 ${
                canOpen ? 'cursor-pointer active:bg-gray-50 transition-colors duration-150 touch-manipulation' : ''
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {/* Top: Lead + title (the whole card opens the details) */}
              <div className="flex items-center gap-2.5">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
                  <Sparkles className="h-3.5 w-3.5 text-blue-600 stroke-[2.25]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[10px] font-semibold uppercase tracking-[0.08em] text-blue-600 leading-none">
                    Lead
                  </span>
                  <span className="block truncate text-[14px] font-semibold text-gray-900 leading-snug mt-0.5">
                    {opportunity.title}
                    {valueLabel ? (
                      <span className="font-normal text-gray-400"> · {valueLabel}</span>
                    ) : null}
                  </span>
                </span>
                {canOpen ? (
                  <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0 -mr-0.5" strokeWidth={2.25} />
                ) : null}
              </div>

              {/* Bottom: stage dropdown + follow-up, centered as a balanced pair */}
              <div
                className="mt-1 flex items-center justify-center gap-2"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                {canChangeStage ? (
                  <StagePill
                    stages={opportunity.stages}
                    currentStageId={opportunity.stage_id}
                    currentStageName={opportunity.stage_name}
                    onChange={(newStageId) => onChangeStage!(opportunity.id, newStageId)}
                  />
                ) : opportunity.stage_name ? (
                  <span className="flex-shrink-0 max-w-[42vw] sm:max-w-[12rem] truncate rounded-full bg-black/[0.05] px-3 py-1.5 text-[12px] font-semibold text-gray-600 leading-none">
                    {opportunity.stage_name}
                  </span>
                ) : null}

                {showFollowUp ? (
                  <div className="min-w-0">
                    <OpportunityNextFollowUpHeader
                      compact
                      nextFollowUpAt={opportunity.next_follow_up_at ?? null}
                      onSave={(followUpAt) => onSaveFollowUp!(opportunity.id, followUpAt)}
                      onComplete={
                        onCompleteFollowUp
                          ? () => onCompleteFollowUp(opportunity.id)
                          : undefined
                      }
                    />
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
